import React, { useState, useEffect, useCallback } from "react";
import {
  Form,
  Input,
  Select,
  TreeSelect,
  InputNumber,
  Row,
  Col,
  Button,
  Upload,
  message,
  Typography,
  Spin,
  Divider,
  Popconfirm,
  Tooltip,
  Image,
  Tag,
  theme,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  PictureOutlined,
} from "@ant-design/icons";
import type { UploadProps } from "antd";

import { CategoryTreeNode } from "types/notice/CategoryDto";
import { getCategories } from "api/services/odata/categoriesOdataService";
import { buildCategoryTree } from "utils/buildCategoryTree";
import { NoticeSectionUpdateDto, UpdateSection } from "api/services/noticeService";
import {
  uploadMedia,
  getMediaByAlbumId,
  deleteMedia,
  setDefaultMedia,
  MediaItemDto,
} from "api/services/mediaService";
import apiClient from "api/client/axios.client";

const { Title, Text } = Typography;

export enum NoticeState {
  PreDraft = 0,
  Draft = 1,
  Published = 2,
  Completed = 3,
  Finished = 4,
}

export enum Shipping {
  None = 0,
  ByAdvertiser = 1,
  ByProvider = 2,
}

const SHIPPING_OPTIONS = [
  { label: "حمل و نقل توافقی / ندارد", value: Shipping.None },
  { label: "ارسال توسط فروشنده (هنرمند / گالری)", value: Shipping.ByAdvertiser },
  { label: "تحویل حضوری یا با خریدار", value: Shipping.ByProvider },
];

export interface NoticeHeaderDto {
  Id: string;
  Title?: string;
  CategoryId?: string;
  Description?: string;
  Price?: number;
  ArtistName?: string;
  Year?: number;
  Length?: number;
  Width?: number;
  Height?: number;
  Shipping?: Shipping | string | number;
  ExpirationDays?: number;
  AlbumId?: string;
}

const CustomLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text strong style={{ display: "block", marginBottom: 6 }}>
    {children}
  </Text>
);

const getPreDraftNoticeOData = async (): Promise<NoticeHeaderDto | null> => {
  try {
    const response = await apiClient.get<any>(
      "/Notice/odata/NoticeHeaderOData?$filter=NoticeState eq 'PreDraft'&$top=1&$expand=Category($select=LocalizedName)"
    );
    const items = response.data?.value || response.data;
    return Array.isArray(items) && items.length > 0 ? items[0] : null;
  } catch (error) {
    console.error("Error fetching pre-draft notice:", error);
    return null;
  }
};

const NewArtworkNotice: React.FC = () => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [categoriesTree, setCategoriesTree] = useState<CategoryTreeNode[]>([]);
  const [noticeId, setNoticeId] = useState<string | null>(null);
  const [images, setImages] = useState<MediaItemDto[]>([]);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [loadingField, setLoadingField] = useState<string | null>(null);

  const fetchAlbumImages = useCallback(async (albumId: string) => {
    try {
      const mediaList = await getMediaByAlbumId(albumId);
      setImages(mediaList || []);
    } catch (error) {
      console.error("Error loading images:", error);
      message.error("خطا در بارگذاری تصاویر آلبوم.");
    }
  }, []);

  const loadPreDraft = useCallback(async (isAfterSubmit: boolean = false) => {
    try {
      setIsPageLoading(true);
      const preDraft = await getPreDraftNoticeOData();

      if (preDraft) {
        setNoticeId(preDraft.Id);

        let shippingVal = Shipping.None;
        if (typeof preDraft.Shipping === "string") {
          shippingVal = Shipping[preDraft.Shipping as keyof typeof Shipping] ?? Shipping.None;
        } else if (typeof preDraft.Shipping === "number") {
          shippingVal = preDraft.Shipping;
        }

        form.setFieldsValue({
          title: preDraft.Title,
          categoryId: preDraft.CategoryId,
          artistName: preDraft.ArtistName,
          year: preDraft.Year || undefined,
          price: preDraft.Price,
          length: preDraft.Length,
          width: preDraft.Width,
          height: preDraft.Height,
          description: preDraft.Description,
          shipping: shippingVal,
          expirationDays: preDraft.ExpirationDays ?? 30,
          albumId: preDraft.AlbumId,
        });

        if (preDraft.AlbumId) {
          await fetchAlbumImages(preDraft.AlbumId);
        } else {
          setImages([]);
        }

        if (isAfterSubmit) {
          message.info("یک پیش‌نویس دیگر بازیابی شد.");
        } else {
          message.info("اطلاعات پیش‌نویس قبلی شما بازیابی شد.");
        }
      } else {
        setNoticeId(null);
        setImages([]);
        form.resetFields();
      }
    } catch (error) {
      console.error("Error fetching predraft:", error);
      message.error("خطا در بررسی پیش‌نویس‌ها.");
    } finally {
      setIsPageLoading(false);
    }
  }, [form, fetchAlbumImages]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsPageLoading(true);
        const categoriesData = await getCategories();
        const formattedTree = buildCategoryTree(categoriesData);
        setCategoriesTree(formattedTree);
      } catch (error) {
        console.error("Error loading categories:", error);
        message.error("خطا در بارگذاری سبک‌ها و دسته‌بندی‌ها.");
      } finally {
        setIsPageLoading(false);
      }
      await loadPreDraft();
    };

    loadInitialData();
  }, [loadPreDraft]);

  const handleSectionUpdate = async (key: string, value: any) => {
    if (value === undefined || value === null || value === "") return;
    const stringValue = typeof value === "object" ? JSON.stringify(value) : String(value);

    try {
      setLoadingField(key);
      const payload = {
        noticeId: noticeId,
        key: key.toLowerCase(),
        value: stringValue,
      } as unknown as NoticeSectionUpdateDto;

      const response: any = await UpdateSection(payload);
      const responseId = response?.id || response?.Id;

      if (responseId && !noticeId) {
        setNoticeId(responseId);
      }

      message.success({
        content: "اطلاعات با موفقیت ذخیره شد.",
        key: "save_status",
        duration: 1.2,
      });
    } catch (error) {
      console.error("Error updating section:", error);
      message.error("خطا در ذخیره‌سازی خودکار اطلاعات!");
    } finally {
      setLoadingField(null);
    }
  };

  const handleSave = async (values: any) => {
    try {
      setIsSubmitting(true);

      let currentNoticeId = noticeId;

      if (!currentNoticeId) {
        const createPayload = {
          noticeId: null,
          key: "title",
          value: values.title,
        } as unknown as NoticeSectionUpdateDto;

        const res: any = await UpdateSection(createPayload);
        const resId = res?.id || res?.Id;

        if (resId) {
          currentNoticeId = resId;
          setNoticeId(resId);
        }
      }

      const payload = {
        noticeId: currentNoticeId,
        key: "noticestate",
        value: String(NoticeState.Published),
      } as unknown as NoticeSectionUpdateDto;

      await UpdateSection(payload);
      message.success("اثر هنری با موفقیت منتشر شد.");
      await loadPreDraft(true);
    } catch (error) {
      console.error("Submit Error:", error);
      message.error("خطا در انتشار اثر هنری. لطفاً مجدداً تلاش کنید.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;

    try {
      setIsUploading(true);
      const currentAlbumId = form.getFieldValue("albumId");

      const response: any = await uploadMedia(currentAlbumId, file as File);

      const newAlbumId =
        response?.albumId ||
        response?.AlbumId ||
        response?.data?.albumId ||
        response?.data?.AlbumId;

      if (newAlbumId) {
        if (!currentAlbumId) {
          form.setFieldValue("albumId", newAlbumId);
          await handleSectionUpdate("albumid", newAlbumId);
        }
        await fetchAlbumImages(newAlbumId);
      } else if (currentAlbumId) {
        await fetchAlbumImages(currentAlbumId);
      }

      message.success(`${(file as File).name} با موفقیت بارگذاری شد.`);
      onSuccess?.(response, file);
    } catch (error: any) {
      console.error("Upload error:", error);
      message.error(`خطا در آپلود فایل ${(file as File)?.name || ""}`);
      onError?.(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImage = async (mediaId: string) => {
    try {
      await deleteMedia(mediaId);
      message.success("تصویر با موفقیت حذف شد.");
      const currentAlbumId = form.getFieldValue("albumId");
      if (currentAlbumId) {
        await fetchAlbumImages(currentAlbumId);
      }
    } catch (error) {
      console.error("Delete media error:", error);
      message.error("خطا در حذف تصویر.");
    }
  };

  const handleSetDefaultCover = async (mediaId: string) => {
    const currentAlbumId = form.getFieldValue("albumId");
    if (!currentAlbumId) return;

    try {
      await setDefaultMedia(currentAlbumId, mediaId);
      message.success("تصویر به عنوان کاور اصلی انتخاب شد.");
      await fetchAlbumImages(currentAlbumId);
    } catch (error) {
      console.error("Set default media error:", error);
      message.error("خطا در تنظیم کاور اصلی.");
    }
  };

  const uploadProps: UploadProps = {
    name: "files",
    multiple: true,
    showUploadList: false,
    customRequest: handleCustomUpload,
    beforeUpload(file) {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("تنها مجاز به آپلود فایل‌های تصویری هستید!");
      }
      return isImage || Upload.LIST_IGNORE;
    },
  };

  return (
    <Spin spinning={isPageLoading} tip="در حال بارگذاری اطلاعات..." size="large">
      <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto", minHeight: "60vh" }}>
        <Title level={3} style={{ marginBottom: 20 }}>
          ثبت و معرفی اثر هنری
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            shipping: Shipping.None,
            expirationDays: 30,
          }}
        >
          <Form.Item name="albumId" hidden>
            <Input />
          </Form.Item>

          {/* ======================= بخش گالری و تصاویر اثر (بالای فرم) ======================= */}
          <div
            style={{
              background: token.colorFillAlter,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: 12,
              padding: 20,
              marginBottom: 32,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <Text strong style={{ fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <PictureOutlined style={{ color: token.colorPrimary }} /> تصاویر اثر هنری
                </Text>
                <Text type="secondary" style={{ fontSize: 13, marginTop: 4, display: "block" }}>
                  تصویر اول دارای ستاره طلایی به عنوان کاور اصلی اثر در لیست‌ها و صفحه اول نمایش داده می‌شود.
                </Text>
              </div>
              {images.length > 0 && (
                <Tag color="processing" style={{ borderRadius: 12, padding: "2px 10px" }}>
                  {images.length} تصویر بارگذاری شده
                </Tag>
              )}
            </div>

            <Row gutter={[16, 16]} align="stretch">
              <Image.PreviewGroup>
                {images.map((item) => {
                  const imageSrc = `data:${item.mimeType || "image/jpeg"};base64,${
                    item.thumbnail || item.medium
                  }`;

                  return (
                    <Col xs={12} sm={8} md={6} lg={4.8 as any} key={item.id}>
                      <div
                        style={{
                          position: "relative",
                          height: 150,
                          borderRadius: 8,
                          overflow: "hidden",
                          border: item.isDefault
                            ? "2px solid #faad14"
                            : `1px solid ${token.colorBorderSecondary}`,
                          boxShadow: item.isDefault ? "0 4px 12px rgba(250, 173, 20, 0.25)" : "none",
                          background: "#000",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Image
                          src={imageSrc}
                          alt="artwork"
                          style={{
                            width: "100%",
                            height: 150,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />

                        {/* برچسب کاور */}
                        {item.isDefault && (
                          <div
                            style={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              background: "rgba(0, 0, 0, 0.75)",
                              color: "#faad14",
                              padding: "2px 8px",
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              backdropFilter: "blur(4px)",
                              zIndex: 2,
                            }}
                          >
                            <StarFilled /> کاور اصلی
                          </div>
                        )}

                        {/* اکشن‌های روی تصویر */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)",
                            padding: "8px 6px 6px 6px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            zIndex: 2,
                          }}
                        >
                          {item.isDefault ? (
                            <Tooltip title="کاور پیش‌فرض است">
                              <Button
                                type="text"
                                shape="circle"
                                size="small"
                                icon={<StarFilled style={{ color: "#faad14", fontSize: 16 }} />}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="تنظیم به عنوان کاور">
                              <Button
                                type="text"
                                shape="circle"
                                size="small"
                                icon={<StarOutlined style={{ color: "#fff", fontSize: 16 }} />}
                                onClick={() => handleSetDefaultCover(item.id)}
                              />
                            </Tooltip>
                          )}

                          <Popconfirm
                            title="حذف تصویر"
                            description="آیا از حذف این تصویر مطمئن هستید؟"
                            onConfirm={() => handleDeleteImage(item.id)}
                            okText="بله"
                            cancelText="خیر"
                          >
                            <Tooltip title="حذف تصویر">
                              <Button
                                type="text"
                                danger
                                shape="circle"
                                size="small"
                                icon={<DeleteOutlined style={{ fontSize: 15 }} />}
                              />
                            </Tooltip>
                          </Popconfirm>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Image.PreviewGroup>

              {/* جعبه آپلود چسبیده به لیست تصاویر */}
              <Col xs={12} sm={8} md={6} lg={4.8 as any}>
                <Upload {...uploadProps} style={{ width: "100%", height: "100%" }}>
                  <div
                    style={{
                      height: 150,
                      border: `2px dashed ${token.colorBorderSecondary}`,
                      borderRadius: 8,
                      background: token.colorBgContainer,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      gap: 8,
                      transition: "border-color 0.3s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = token.colorPrimary)}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = token.colorBorderSecondary)}
                  >
                    {isUploading ? (
                      <Spin size="small" tip="درحال آپلود..." />
                    ) : (
                      <>
                        <PlusOutlined style={{ fontSize: 24, color: token.colorTextSecondary }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          افزودن تصویر
                        </Text>
                      </>
                    )}
                  </div>
                </Upload>
              </Col>
            </Row>
          </div>

          {/* ======================= اطلاعات اصلی اثر ======================= */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <div>
                <CustomLabel>عنوان اثر هنری</CustomLabel>
                <Form.Item
                  name="title"
                  rules={[{ required: true, message: "وارد کردن عنوان اثر الزامی است" }]}
                  extra={loadingField === "title" ? <Spin size="small" /> : null}
                >
                  <Input
                    placeholder="مثال: تابلوی نقاشی رقص سماع"
                    size="large"
                    onBlur={(e) => handleSectionUpdate("title", e.target.value)}
                  />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <div>
                <CustomLabel>سبک / دسته‌بندی اثر</CustomLabel>
                <Form.Item
                  name="categoryId"
                  rules={[{ required: true, message: "انتخاب دسته‌بندی الزامی است" }]}
                  extra={loadingField === "categoryid" ? <Spin size="small" /> : null}
                >
                  <TreeSelect
                    size="large"
                    style={{ width: "100%" }}
                    treeData={categoriesTree}
                    placeholder="انتخاب شاخه هنری (نقاشی، خطاطی، مجسمه و...)"
                    treeDefaultExpandAll
                    treeNodeFilterProp="title"
                    onChange={(val) => handleSectionUpdate("categoryid", val)}
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <div>
                <CustomLabel>نام هنرمند / پدیدآورنده</CustomLabel>
                <Form.Item
                  name="artistName"
                  rules={[{ required: true, message: "نام هنرمند الزامی است" }]}
                  extra={loadingField === "artistname" ? <Spin size="small" /> : null}
                >
                  <Input
                    placeholder="نام و نام خانوادگی هنرمند"
                    size="large"
                    onBlur={(e) => handleSectionUpdate("artistname", e.target.value)}
                  />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div>
                <CustomLabel>سال خلق اثر</CustomLabel>
                <Form.Item
                  name="year"
                  rules={[{ required: true, message: "سال ساخت اثر الزامی است" }]}
                  extra={loadingField === "year" ? <Spin size="small" /> : null}
                >
                  <InputNumber
                    placeholder="مثال: 1402 یا 2023"
                    size="large"
                    style={{ width: "100%" }}
                    onBlur={() => handleSectionUpdate("year", form.getFieldValue("year"))}
                  />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div>
                <CustomLabel>قیمت پیشنهادی (تومان)</CustomLabel>
                <Form.Item
                  name="price"
                  rules={[{ required: true, message: "تعیین قیمت الزامی است" }]}
                  extra={loadingField === "price" ? <Spin size="small" /> : null}
                >
                  <InputNumber
                    size="large"
                    style={{ width: "100%" }}
                    placeholder="مثال: 25,000,000"
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => (value ? Number(value.replace(/\$\s?|(,*)/g, "")) : 0)}
                    onBlur={() => handleSectionUpdate("price", form.getFieldValue("price"))}
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>

          <Divider orientation="right">ابعاد و مشخصات فیزیکی (سانتی‌متر)</Divider>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <div>
                <CustomLabel>طول (Length)</CustomLabel>
                <Form.Item
                  name="length"
                  extra={loadingField === "length" ? <Spin size="small" /> : null}
                >
                  <InputNumber
                    size="large"
                    placeholder="طول به سانتی‌متر"
                    style={{ width: "100%" }}
                    min={0}
                    precision={1}
                    onBlur={() => handleSectionUpdate("length", form.getFieldValue("length"))}
                  />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div>
                <CustomLabel>عرض (Width)</CustomLabel>
                <Form.Item
                  name="width"
                  extra={loadingField === "width" ? <Spin size="small" /> : null}
                >
                  <InputNumber
                    size="large"
                    placeholder="عرض به سانتی‌متر"
                    style={{ width: "100%" }}
                    min={0}
                    precision={1}
                    onBlur={() => handleSectionUpdate("width", form.getFieldValue("width"))}
                  />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div>
                <CustomLabel>ارتفاع / عمق (Height)</CustomLabel>
                <Form.Item
                  name="height"
                  extra={loadingField === "height" ? <Spin size="small" /> : null}
                >
                  <InputNumber
                    size="large"
                    placeholder="ارتفاع/عمق به سانتی‌متر"
                    style={{ width: "100%" }}
                    min={0}
                    precision={1}
                    onBlur={() => handleSectionUpdate("height", form.getFieldValue("height"))}
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>

          <Divider orientation="right">توضیحات و شرایط ارسال</Divider>

          <Row gutter={16}>
            <Col xs={24}>
              <div>
                <CustomLabel>داستان و توضیحات تکمیلی اثر (تکنیک، متریال، سبک و اصالت)</CustomLabel>
                <Form.Item
                  name="description"
                  rules={[{ required: true, message: "وارد کردن توضیحات اثر الزامی است" }]}
                  extra={loadingField === "description" ? <Spin size="small" /> : null}
                >
                  <Input.TextArea
                    placeholder="در مورد سبک نقاشی، رنگ‌های به‌کاررفته، متریال بوم یا مجسمه و شناسنامه اثر بنویسید..."
                    autoSize={{ minRows: 4, maxRows: 8 }}
                    size="large"
                    onBlur={(e) => handleSectionUpdate("description", e.target.value)}
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <div>
                <CustomLabel>نحوه تحویل و ارسال</CustomLabel>
                <Form.Item
                  name="shipping"
                  extra={loadingField === "shipping" ? <Spin size="small" /> : null}
                >
                  <Select
                    size="large"
                    style={{ width: "100%" }}
                    options={SHIPPING_OPTIONS}
                    onChange={(val) => handleSectionUpdate("shipping", val)}
                  />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <div>
                <CustomLabel>مدت زمان اعتبار نمایش آگهی (روز)</CustomLabel>
                <Form.Item
                  name="expirationDays"
                  rules={[{ required: true, message: "تعداد روزهای انقضا الزامی است" }]}
                  extra={loadingField === "expirationdays" ? <Spin size="small" /> : null}
                >
                  <InputNumber
                    size="large"
                    min={1}
                    max={365}
                    style={{ width: "100%" }}
                    onBlur={() =>
                      handleSectionUpdate("expirationdays", form.getFieldValue("expirationDays"))
                    }
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={24} style={{ textAlign: "end", marginTop: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ minWidth: 180 }}
                loading={isSubmitting}
              >
                ثبت و انتشار اثر هنری
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </Spin>
  );
};

export default NewArtworkNotice;
